import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  DurationType,
  EntitlementOption,
  containerProductHourlyAndUsage,
  ScheduleType,
} from '../../../../modules/privateOffer/types'
import { getAmiInstallmentsBasedOnSchedule } from '../../../../utils/amiPrivateOfferUtil'
import {
  DatePickerV2,
  LabraToolTipV2,
  labraUIDateFormat,
  BoxV4,
  PlanType,
  RadioButtonsV2,
  RadioButtonType,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'
import { Divider, SelectChangeEvent, Typography } from '@mui/material'
import { useStyles } from './AdditionalPaymentConfiguration.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faListCheck } from '@fortawesome/pro-regular-svg-icons'
import {
  ContainerPricingModel,
  Product,
} from '../../../../modules/productsListing/types'
import { OffersWithErrors } from '../../../../modules/privateOffer/privateOfferCreation/types'
import { useSelector } from '../../../../store'
import { FlyOutListingData } from '../../../../types/flyout'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { NetPaymentTermsField } from '../../../../common/components/NetPaymentTermsField/NetPaymentTermsField'
import { MultiCurrencyForm } from '../../../../common/components/MultiCurrencyForm/MultiCurrencyForm'
import { DateTime } from 'luxon'
import { calculateDurationInMonths } from '../AmiPrivateOfferInstallmentsSection/AmiPrivateOfferInstallmentsSection'
import clsx from 'clsx'

export type AdditionalPaymentConfigurationProps = {
  product: Product
  noteDescription?: string
}
export const ContractDurationOptions = [
  {
    label: 'Days',
    value: 'DAYS',
  },
  {
    label: 'Weeks',
    value: 'WEEKS',
  },
  {
    label: 'Months',
    value: 'MONTHS',
  },
  {
    label: 'Years',
    value: 'YEARS',
  },
]

const BOX_HEADING_ICON_STYLE = { width: '18px', height: '14px' } as const

const DATE_PICKER_VIEWS: Array<'year' | 'month' | 'day'> = [
  'year',
  'month',
  'day',
]

const DURATION_LENGTH_KEYS_TO_PREVENT = ['+', '-', 'e', 'E', '.']

/** Single empty dimension row reused when resetting dimensions (stable reference). */
const EMPTY_DIMENSION_ROW = [
  {
    label: '',
    labelDescription: '',
    type: 'standard' as const,
    isAdditionalUsageAllowed: false,
    currency: 'USD',
  },
]

const FLEXIBLE_PAYMENT_RADIO_MAPPINGS = [
  {
    key: 'flex-true',
    label: 'Yes',
    type: RadioButtonType.STANDARD,
  },
  {
    key: 'flex-false',
    label: 'No',
    type: RadioButtonType.STANDARD,
  },
]

const AdditionalPaymentConfigurationInner: React.FC<
  AdditionalPaymentConfigurationProps
> = ({ product, noteDescription }) => {
  const formik = useFormikContext() as FormikProps<OffersWithErrors>
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    setTouched,
    setValues,
  } = formik
  const formikRecordProps = formik as unknown as FormikProps<
    Record<string, unknown>
  >
  const flyoutSellerInfo = useSelector<FlyOutListingData>(
    state => state.flyoutSellerInfo
  )
  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === false
      ) || []
    )
  }, [product])
  useEffect(() => {
    const areInstallmentsEmpty = !values?.installments?.length
    if (values?.flexiblePaymentTerms && areInstallmentsEmpty) {
      setFieldValue('installments', [
        { paymentDate: '', amount: null, currency: 'USD' },
      ])
    }
    setFieldTouched('installments', false)
  }, [values?.flexiblePaymentTerms])

  const handleFlexiblePaymentTermsChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange({
        target: {
          name: 'flexiblePaymentTerms',
          value: e.target.value === 'flex-true' ? true : false,
        },
      })
      if (product.containerPricingModel === ContainerPricingModel.CONTRACT) {
        await setFieldValue('entitlementOption', EntitlementOption.SINGLE)
      }
      const additionalDimensions = (values?.dimensions || []).filter(
        dimension => dimension.isAdditionalUsageAllowed === true
      )

      if (product.containerPricingModel === ContainerPricingModel.CONTRACT) {
        await setFieldValue('containerOfferServiceLength', '12M')
      } else {
        await setFieldValue('containerOfferServiceLength', '1Y')
      }

      if (
        product.containerPricingModel &&
        containerProductHourlyAndUsage.includes(product.containerPricingModel)
      ) {
        await setFieldValue('usageDuration', '')
        await setFieldTouched('usageDuration', false)
      }

      if (
        product.containerPricingModel === ContainerPricingModel.HOURLY ||
        product.containerPricingModel === ContainerPricingModel.USAGE
      ) {
        if (
          e.target.value === 'flex-true' &&
          product.containerPricingModel === ContainerPricingModel.USAGE
        ) {
          const updatedDimensions = [
            ...EMPTY_DIMENSION_ROW,
            ...additionalDimensions,
          ]
          await setFieldValue('dimensions', updatedDimensions)
        } else {
          const dimensionsWithType = productDimensions.map(dimension => ({
            ...dimension,
            type: 'standard',
            dimensionId: dimension?.id,
          }))
          const updatedDimensions = [
            ...dimensionsWithType,
            ...additionalDimensions,
          ]
          await setFieldValue('dimensions', updatedDimensions)
        }
        await setFieldTouched('dimensions', false)
      }
    },
    [
      handleChange,
      product.containerPricingModel,
      productDimensions,
      setFieldTouched,
      setFieldValue,
      values?.dimensions,
    ]
  )

  const contractDurationSelectField = useMemo(
    () => ({
      title: 'Duration type',
      placeHolder: 'Duration type',
      humanReadableKey: 'Duration type',
      id: 'durationType',
      isRequired: false,
      isReadOnly: false,
      isCustom: false,
      defaultValue: '',
      dataType: 'enum' as DataType,
      options: ContractDurationOptions,
    }),
    []
  )

  const durationTypeInfoTooltip = useMemo(
    () => ({
      title: '',
      content: 'Select the time unit for the contract duration.',
    }),
    []
  )

  const amiPricingRadioMappings = useMemo(
    () => [
      {
        key: 'upfront-payment',
        label: 'Upfront payment',
        type: RadioButtonType.STANDARD,
      },
      {
        key: 'installment-plan',
        label: 'Installment plan',
        type: RadioButtonType.STANDARD,
      },
      {
        key: 'usage-pricing',
        label: 'Usage pricing',
        type: RadioButtonType.STANDARD,
        disabled:
          product.containerPricingModel === ContainerPricingModel.CONTRACT,
        disabledTooltip:
          product.containerPricingModel === ContainerPricingModel.CONTRACT
            ? 'Usage pricing is disabled for contract based products.'
            : '',
      },
    ],
    [product.containerPricingModel]
  )

  const usageEndDateFieldConfig = useMemo(
    () => ({
      title: 'Usage end date',
      humanReadableKey: 'Usage end date',
      placeHolder: 'Usage end date',
      id: 'usageEndDate',
      isRequired: false,
      isReadOnly: false,
      isCustom: false,
      minDate: DateTime.now().plus({ days: 1 }).toISODate(),
    }),
    []
  )

  const flexiblePaymentCheckedValue = useMemo(
    () => ({
      key: values?.flexiblePaymentTerms
        ? 'flexiblePaymentTerms-true'
        : 'flexiblePaymentTerms-false',
      value: values?.flexiblePaymentTerms ? 'flex-true' : 'flex-false',
    }),
    [values?.flexiblePaymentTerms]
  )

  const pricingModelCheckedValue = useMemo(
    () => ({
      key: values?.pricingModel || '',
      value: values?.pricingModel || '',
    }),
    [values?.pricingModel]
  )
  // Helper function to regenerate installments
  const regenerateInstallments = useCallback(
    (containerOfferServiceLength: string) => {
      const durationInMonths = calculateDurationInMonths(
        containerOfferServiceLength
      )
      const paymentSchedule =
        values?.flexiblePaymentType || ScheduleType.MONTHLY
      const offerStartDate =
        values?.firstInstallmentDate || new Date().toISOString().split('T')[0]
      const tcv = values?.tcv

      // Only regenerate if not CUSTOM and we have required values
      if (paymentSchedule !== ScheduleType.CUSTOM && durationInMonths && tcv) {
        const newInstallments = getAmiInstallmentsBasedOnSchedule({
          tcv: tcv || 0,
          durationInMonths: durationInMonths || 0,
          paymentSchedule: paymentSchedule as ScheduleType,
          now: offerStartDate,
        })

        // Set first installment paymentDate from firstInstallmentDate if available (for AMI)
        if (values?.firstInstallmentDate && newInstallments.length > 0) {
          newInstallments[0].paymentDate = values?.firstInstallmentDate
        }

        setFieldValue('installments', newInstallments)
      }
    },
    [
      values?.flexiblePaymentType,
      values?.firstInstallmentDate,
      values?.tcv,
      setFieldValue,
      product.productType,
    ]
  )
  const getDurationInitialValue = useCallback((durationType: DurationType) => {
    switch (durationType) {
      case DurationType.MONTHS:
        return 'M'
      case DurationType.YEARS:
        return 'Y'
      case DurationType.DAYS:
        return 'D'
      case DurationType.WEEKS:
        return 'W'
      default:
        return ''
    }
  }, [])

  const handleChangeSelect = useCallback(
    async (event: SelectChangeEvent) => {
      const selectedValues = event.target.value as string
      await setFieldValue('durationType', selectedValues)
      if (typeof values?.durationLength === 'number') {
        const newServiceLength = `${values?.durationLength}${getDurationInitialValue(selectedValues as DurationType)}`
        await setFieldValue('containerOfferServiceLength', newServiceLength)
        //Regenerate installments with new duration type if durationLength exists
        if (typeof values?.durationLength === 'number') {
          regenerateInstallments(newServiceLength)
        }
      }
    },
    [
      setFieldValue,
      values?.durationLength,
      regenerateInstallments,
      getDurationInitialValue,
    ]
  )

  const handleDurationChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      if (value === '' || !isNaN(Number(value))) {
        const numericValue = value === '' ? '' : Number(value)
        await setFieldValue('durationLength', numericValue)
        if (
          numericValue !== '' &&
          typeof numericValue === 'number' &&
          numericValue >= 0
        ) {
          const newServiceLength = `${numericValue}${getDurationInitialValue(values?.durationType as DurationType)}`
          await setFieldValue('containerOfferServiceLength', newServiceLength)
          if (values?.durationType) {
            regenerateInstallments(newServiceLength)
          }
        }
      }
    },
    [
      setFieldValue,
      values?.durationType,
      regenerateInstallments,
      getDurationInitialValue,
    ]
  )

  const durationText = useMemo(() => {
    switch (values?.durationType as DurationType) {
      case DurationType.MONTHS:
        return 'months'
      case DurationType.YEARS:
        return 'years'
      case DurationType.DAYS:
        return 'days'
      case DurationType.WEEKS:
        return 'weeks'
      default:
        return ''
    }
  }, [values?.durationType])

  const handlePricingModelTypeChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const clickedKey = e.target.value

      if (!clickedKey) {
        await setFieldValue('pricingModel', values?.pricingModel)
        return
      }
      await setValues({
        ...values,
        pricingModel: clickedKey,
        flexiblePaymentTerms: clickedKey === 'installment-plan',
        durationType: DurationType.DAYS,
        durationLength: undefined,
        usageEndDate: undefined,
        containerOfferServiceLength:
          clickedKey === 'upfront-payment' ? '0D' : undefined,
        usageDuration: undefined,
      })
      await setTouched({
        ...touched,
        flexiblePaymentTerms: true,
        pricingModel: false,
        durationType: false,
        durationLength: false,
        usageEndDate: false,
        containerOfferServiceLength: false,
        usageDuration: false,
      })
    },
    [setFieldValue, setTouched, setValues, touched, values]
  )

  const { classes } = useStyles()
  return (
    <div data-testid="flexible-section">
      <BoxV4
        heading={'Currency and Billing configuration'}
        headingIcon={
          <FontAwesomeIcon icon={faListCheck} style={BOX_HEADING_ICON_STYLE} />
        }
      >
        <MultiCurrencyForm
          formik={formikRecordProps}
          flyoutSellerInfo={flyoutSellerInfo}
          noteDescription={noteDescription}
        />
        {!(
          product.productType === PlanType.SAAS_SUBSCRIPTION ||
          product.productType === PlanType.AMI ||
          product.productType === PlanType.SAAS_CONTRACT
        ) && (
          <div className={classes.fullWidthContainer}>
            <Divider className={classes.divider} />
            <div className={classes.sectionContainer}>
              <Typography className={classes.generalText}>
                Allow buyer to pay in installments under flexible payment
                schedule?
              </Typography>
              <div data-testid="flexiblePaymentTerms">
                <RadioButtonsV2
                  radioButtonMappings={FLEXIBLE_PAYMENT_RADIO_MAPPINGS}
                  checkedValue={flexiblePaymentCheckedValue}
                  handleChange={handleFlexiblePaymentTermsChange}
                />
              </div>
            </div>
          </div>
        )}
        {product.productType === PlanType.AMI && (
          <div
            data-testid="ami-pricing-section"
            className={classes.fullWidthContainer}
          >
            <Divider className={classes.divider} />
            <div className={classes.subSectionContainer}>
              <div className={classes.subSection}>
                <div className={classes.inLineInfo}>
                  <Typography className={classes.headingText}>
                    Pricing model
                  </Typography>
                  <LabraToolTipV2
                    tooltipPlacement="top-start"
                    description="Select whether the buyer will be billed upfront, through installments, or based on their usage."
                    tooltipPlacmentGap="13px"
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={classes.info}
                      data-testid="prerequisites-config-tooltip"
                    />
                  </LabraToolTipV2>
                </div>
                <div data-testid="ami-pricing-model-options">
                  <RadioButtonsV2
                    radioButtonMappings={amiPricingRadioMappings}
                    checkedValue={pricingModelCheckedValue}
                    handleChange={handlePricingModelTypeChange}
                    gapBetween="32px"
                  />
                </div>
              </div>
              <div className={classes.subSection}>
                <div className={classes.inLineInfo}>
                  <Typography className={classes.headingText}>
                    {values?.pricingModel === 'usage-pricing'
                      ? 'Usage'
                      : 'Contract duration'}
                  </Typography>
                  <LabraToolTipV2
                    tooltipPlacement="top-start"
                    description={
                      values?.pricingModel === 'usage-pricing'
                        ? ' Select usage end date for this offer.'
                        : 'Select how long the contract will remain active. The price you set will apply only for this duration.'
                    }
                    tooltipPlacmentGap="13px"
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={classes.info}
                      data-testid="prerequisites-config-tooltip"
                    />
                  </LabraToolTipV2>
                </div>
                <div className={classes.fieldRow}>
                  {(values?.pricingModel === 'upfront-payment' ||
                    values?.pricingModel === 'installment-plan') && (
                    <div className={classes.contractDurationFieldsWidth}>
                      <SelectV4
                        field={contractDurationSelectField}
                        value={values?.durationType ?? DurationType.MONTHS}
                        onChange={handleChangeSelect}
                        onBlur={handleBlur}
                        isError={touched.durationType && !!errors.durationType}
                        errorText={
                          (touched.durationType && errors.durationType) || ''
                        }
                        showInsideInfoIcon
                        showInsideInfoIconTooltip={durationTypeInfoTooltip}
                      />
                    </div>
                  )}
                  {(values.pricingModel === 'upfront-payment' ||
                    values.pricingModel === 'installment-plan') && (
                    <div className={classes.contractDurationFieldsWidth}>
                      <TextInputV4
                        keyName="durationLength"
                        title={`Duration(in ${durationText})`}
                        type="number"
                        placeHolder=""
                        value={values?.durationLength ?? ''}
                        updatedValue={values?.durationLength ?? ''}
                        onChange={handleDurationChange}
                        onBlur={handleBlur}
                        isError={
                          !!touched.durationLength && !!errors.durationLength
                        }
                        errorText={
                          (!!touched.durationLength && errors.durationLength) ||
                          ''
                        }
                        isReadOnly={false}
                        isRequired={false}
                        keyToPrevent={DURATION_LENGTH_KEYS_TO_PREVENT}
                        showSpinner={false}
                      />
                    </div>
                  )}
                  {(values?.pricingModel === 'upfront-payment' ||
                    values?.pricingModel === 'usage-pricing') && (
                    <div className={classes.contractDurationFieldsWidth}>
                      <DatePickerV2
                        format={labraUIDateFormat}
                        views={DATE_PICKER_VIEWS}
                        field={usageEndDateFieldConfig}
                        key={'usageEndDate'}
                        showErrorHelper={
                          !!touched.usageEndDate && !!errors.usageEndDate
                        }
                        fieldId={'usageEndDate'}
                        formik={formikRecordProps}
                        errorHelperText={
                          (!!touched.usageEndDate && errors.usageEndDate) || ''
                        }
                        size="small"
                        validate
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <Divider className={clsx(classes.divider, classes.dividerMargin)} />
        <NetPaymentTermsField context="offer" />
      </BoxV4>
    </div>
  )
}

export const AdditionalPaymentConfiguration = memo(
  AdditionalPaymentConfigurationInner
)
AdditionalPaymentConfiguration.displayName = 'AdditionalPaymentConfiguration'
